import type { ListLabelsScope } from '../listLabelsTypes.ts';

/**
 * Legacy Confluence token that, when supplied as the `spaceKey` macro param,
 * means "collect labels from every space" rather than a single space.
 */
export const TOKEN_ALL = '@all';

/**
 * Legacy Confluence sentinel value that authors / templates may set as the
 * `spaceKey` macro param to mean "use the space the page lives in". The
 * extension handler passes the current page's space key alongside the params;
 * this helper substitutes it in.
 */
export const CURRENT_SPACE_KEY_TOKEN = 'currentSpace()';

/**
 * Resolves the raw `spaceKey` macro param value to the effective scope used to
 * collect labels.
 *
 * Behaviour mirrors the legacy backend macro (`ListLabelsMacro.execute`):
 *   - `@all`                       → all spaces.
 *   - `undefined` / empty          → the space the page lives in (`currentSpaceKey`).
 *   - `'currentSpace()'`           → the space the page lives in (`currentSpaceKey`).
 *   - any other value              → that explicit space key.
 *
 * When the macro is rendered outside any space context (e.g. a personal page)
 * and no current space key is available, `spaceKey` resolves to `undefined`;
 * the data layer surfaces this as an empty result — matching the legacy
 * behaviour where an unresolvable space yields no labels.
 */
export const resolveSpaceScope = (
	rawSpaceKey: string | undefined,
	currentSpaceKey: string | undefined,
): ListLabelsScope => {
	if (rawSpaceKey === TOKEN_ALL) {
		return { isAllSpaces: true, spaceKey: undefined };
	}
	if (!rawSpaceKey || rawSpaceKey === CURRENT_SPACE_KEY_TOKEN) {
		return { isAllSpaces: false, spaceKey: currentSpaceKey };
	}
	return { isAllSpaces: false, spaceKey: rawSpaceKey };
};

/**
 * Parses the raw `excludedLabels` macro param into a de-duplicated list of
 * label names to omit.
 *
 * Mirrors the legacy backend macro which splits the value on commas
 * (trimming surrounding whitespace) and drops empty entries. Label names are
 * case-sensitive, matching the legacy behaviour documented in the macro
 * parameter help text.
 */
export const parseExcludedLabels = (value: string | undefined): string[] => {
	if (!value) {
		return [];
	}
	const seen = new Set<string>();
	const parts = value.split(',');
	for (const raw of parts) {
		const trimmed = raw.trim();
		if (trimmed) {
			seen.add(trimmed);
		}
	}
	return Array.from(seen);
};
